import { directionAPI, directionOrdersApi, promoCodeAPI, usersAPI } from '@/api'
import { jsonConvert } from '@/helpers/jsonConvert'
import { ICourseEnrollmentItem } from '../components/SelectAdditionalCourses/components/SelectCourseCard/SelectCourseCard'
import {
  getDiscount,
  getTariffPriceWithDiscount,
  getTotalPrice,
} from './tariff'

export const fetchCoursesWithTariffs = async (): Promise<ICourse[]> => {
  try {
    const res = await directionAPI.getAll({
      expand: [
        'directionTariffs.tariffPeriods',
        'directionTariffs.tariffWeeks',
        'directionTariffs.tariffDays',
        'teacher',
      ],
    })

    return res?.data?.data ?? []
  } catch (error) {
    console.error('Ошибка загрузки курсов:', error)
    return []
  }
}

export const fetchCourseAndTariffs = async (
  id: number,
): Promise<ICourse | null> => {
  try {
    const res = await directionAPI.get({
      id,
      expand: [
        'directionTariffs.directionTariffNotices',
        'directionTariffs.tariffPeriods',
        'directionTariffs.tariffWeeks',
        'directionTariffs.tariffDays',
        'seniorSeminarians',
      ],
    })

    return {
      ...res.data,
      description: jsonConvert(res.data.description, 'json'),
      directionTariffs:
        res.data.directionTariffs?.map((item: ICourseTariff) => ({
          ...item,
          description: jsonConvert(item.description, 'json'),
        })) ?? [],
    }
  } catch (error) {
    console.error('Ошибка загрузки настроек курса:', error)
    return null
  }
}

export const checkPromoCode = async (
  name: string,
  userId: number | null,
): Promise<IPromoCodeDiscount | null> => {
  try {
    const res = await promoCodeAPI.check(name?.trim(), { id: userId })

    return res?.data?.data ?? null
  } catch (error) {
    console.error('Ошибка проверки промокода:', error)
    return null
  }
}

export interface ICheckEmailOrPhoneResponse {
  user: IUser | null
  roles: string[]
  hasCourse: boolean
  directions: number[]
}

export const checkEmailExist = async (
  email: string,
  courseId?: number,
  classId?: number,
  isParent?: boolean,
): Promise<ICheckEmailOrPhoneResponse> => {
  try {
    const res = await usersAPI.checkEmail(
      email?.trim(),
      courseId,
      classId,
      isParent ?? false,
    )

    return {
      user: res?.data?.data?.user ?? null,
      hasCourse: res?.data?.data?.hasCourse ?? false,
      roles: res?.data?.data?.roles ?? [],
      directions: res?.data?.data?.directions ?? [],
    }
  } catch (error) {
    console.error('Ошибка проверки почты:', error)
    return {
      user: null,
      hasCourse: false,
      roles: [],
      directions: [],
    }
  }
}
export const checkPhoneExist = async (
  phone: string,
  courseId?: number,
  classId?: number,
  isParent?: boolean,
): Promise<ICheckEmailOrPhoneResponse> => {
  try {
    const res = await usersAPI.checkPhone(
      phone?.trim(),
      courseId,
      classId,
      isParent ?? false,
    )

    return {
      user: res?.data?.data?.user ?? null,
      hasCourse: res?.data?.data?.hasCourse ?? false,
      roles: res?.data?.data?.roles ?? [],
      directions: res?.data?.data?.directions ?? [],
    }
  } catch (error) {
    console.error('Ошибка проверки телефона:', error)
    return {
      user: null,
      hasCourse: false,
      roles: [],
      directions: [],
    }
  }
}

export interface IEnrollmentProfileData {
  studentName: string
  studentEmail: string
  studentPhone: string
  password: string
  parentName: string
  parentPhone: string
  parentEmail: string
  parentPassword: string
  customText: string
}

export const courseEnrollment = async (
  courseId: number,
  tariff: ICourseTariff,
  withDemo: boolean,
  promo?: IPromoCodeDiscount,
  value?: number,
  user?: IEnrollmentProfileData,
  token?: string,
  code?: string,
  userAlreadyExists?: boolean,
  addedCourses?: ICourseEnrollmentItem[],
  discounts?: number[],
  discountUnits?: string,
  crmLinkId?: number,
  userId?: number,
): Promise<{ status: 'success' | 'error'; data: PaymentOrder | null }> => {
  try {
    const data = new FormData()
    data.append('course_id', courseId?.toString())
    data.append('tariff_id', tariff?.id!.toString())
    if (promo?.id) data.append('promo_id', promo?.id?.toString())
    if (value) data.append('value', value?.toString())
    if (user) data.append('user', JSON.stringify(user))
    if (!!token) data.append('token', token?.toString())
    if (!!code) data.append('code', code?.toString())
    if (userAlreadyExists) data.append('userAlreadyExists', '1')
    if (withDemo) data.append('has_demo', withDemo?.toString())
    if (crmLinkId) data.append('link_id', crmLinkId?.toString())
    if (userId) data.append('user_id', userId?.toString())

    data.append(
      'price',
      getTariffPriceWithDiscount(tariff, value, promo)?.toString(),
    )

    const totalPrice = getTotalPrice(
      tariff,
      value,
      promo,
      withDemo,
      addedCourses,
      discounts,
      discountUnits,
    )

    data.append('total', (totalPrice ?? 0)?.toString())

    const isFirstFree = tariff?.type === 'free' || withDemo

    if (addedCourses)
      data.append(
        'courses',
        JSON.stringify(
          addedCourses?.map((order, index) => {
            const promocode = getDiscount(
              isFirstFree,
              index,
              addedCourses,
              discounts ?? [],
              discountUnits ?? '',
            )

            const price = getTariffPriceWithDiscount(
              order?.course?.directionTariffs?.find(
                (item) => item?.id === order.tariffId,
              ),
              order.value ?? undefined,
              promocode,
            )

            return {
              course_id: order?.course?.id,
              tariff_id: order?.tariffId,
              value: order.value,
              has_demo: order?.withDemo ?? false,
              price: order?.withDemo ? 0 : price,
              enroll_date: order?.enrollDate ?? null,
            }
          }),
        ),
      )

    const res = await directionOrdersApi.enroll(data)

    return { status: res.data.status, data: res?.data?.data ?? null }
  } catch (error) {
    console.error('Ошибка записи на курс:', error)
    return { status: 'error', data: null }
  }
}
